import React, { useEffect, useState } from 'react';
import { streamsService, LearningStream } from '../services/streams';
import { logsService, DailyLog } from '../services/logs';
import { vocabulariesService, VocabularyCreate } from '../services/vocabularies';
import { practiceTestsService, PracticeTestCreate, SkillType } from '../services/practiceTests';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const GermanTracker: React.FC = () => {
  const [germanStream, setGermanStream] = useState<LearningStream | null>(null);
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [currentLevel, setCurrentLevel] = useState('A1');
  const [vocabularyWords, setVocabularyWords] = useState(0);
  const vocabularyTarget = 100;
  const [confidenceScores, setConfidenceScores] = useState({
    listening: 5,
    speaking: 5,
    reading: 5,
    writing: 5,
  });
  const [loading, setLoading] = useState(true);
  const [showLogForm, setShowLogForm] = useState(false);
  const [newLogHours, setNewLogHours] = useState('');
  const [newLogTopics, setNewLogTopics] = useState('');
  const [showVocabForm, setShowVocabForm] = useState(false);
  const [showTestForm, setShowTestForm] = useState(false);
  const [showExamInfo, setShowExamInfo] = useState(false);
  const [newVocab, setNewVocab] = useState({ word: '', translation: '', example_sentence: '', category: '', difficulty_level: currentLevel });
  const [newTest, setNewTest] = useState({ test_name: '', test_type: 'Mock', skill_tested: 'Listening' as SkillType, scheduled_date: '', duration_minutes: 60 });

  useEffect(() => {
    loadGermanData();
  }, []);

  const loadGermanData = async () => {
    try {
      const streams = await streamsService.getAll();
      const german = streams.find(s => s.name === 'German');
      
      if (german) {
        setGermanStream(german);
        const germanLogs = await logsService.getAll(german.id);
        setLogs(germanLogs);
        
        // Calculate current level based on progress
        const progress = (german.completed_hours / german.target_hours) * 100;
        if (progress < 15) setCurrentLevel('A1');
        else if (progress < 30) setCurrentLevel('A2');
        else if (progress < 50) setCurrentLevel('B1');
        else if (progress < 80) setCurrentLevel('B2');
        else setCurrentLevel('C1');
      }
      
      // Load vocabulary and practice tests
      const [vocabs] = await Promise.all([
        vocabulariesService.getAll(),
        practiceTestsService.getAll(),
      ]);
      
      setVocabularyWords(vocabs.length);
    } catch (error) {
      console.error('Error loading German data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogHours = async () => {
    if (!germanStream || !newLogHours) return;
    
    try {
      await logsService.create({
        stream_id: germanStream.id,
        date: new Date().toISOString().split('T')[0],
        hours_studied: parseFloat(newLogHours),
        topics_covered: newLogTopics ? [newLogTopics] : [],
        confidence_score: Math.round(
          (confidenceScores.listening + confidenceScores.speaking + 
           confidenceScores.reading + confidenceScores.writing) / 4
        ),
      });
      setNewLogHours('');
      setNewLogTopics('');
      setShowLogForm(false);
      loadGermanData();
    } catch (error) {
      console.error('Error logging hours:', error);
      alert('Failed to log hours. Please try again.');
    }
  };

  const handleAddVocabulary = async () => {
    if (!newVocab.word || !newVocab.translation) return;
    
    try {
      const vocabData: VocabularyCreate = {
        ...newVocab,
        date_added: new Date().toISOString().split('T')[0],
      };
      await vocabulariesService.create(vocabData);
      
      setNewVocab({ word: '', translation: '', example_sentence: '', category: '', difficulty_level: currentLevel });
      setShowVocabForm(false);
      loadGermanData();
    } catch (error) {
      console.error('Error adding vocabulary:', error);
      alert('Failed to add vocabulary. Please try again.');
    }
  };

  const handleScheduleTest = async () => {
    if (!newTest.test_name || !newTest.scheduled_date) return;
    
    try {
      const testData: PracticeTestCreate = {
        ...newTest,
        skill_tested: newTest.skill_tested as SkillType,
      };
      await practiceTestsService.create(testData);
      
      setNewTest({ test_name: '', test_type: 'Mock', skill_tested: 'Listening' as SkillType, scheduled_date: '', duration_minutes: 60 });
      setShowTestForm(false);
      loadGermanData();
    } catch (error) {
      console.error('Error scheduling test:', error);
      alert('Failed to schedule test. Please try again.');
    }
  };

  const getExamDate = () => {
    if (currentLevel === 'A1') {
      return new Date('2025-12-15');
    } else if (currentLevel === 'A2') {
      return new Date('2026-02-15');
    } else if (currentLevel === 'B1') {
      return new Date('2026-05-15');
    } else if (currentLevel === 'B2') {
      return new Date('2026-09-15');
    }
    return null;
  };

  const getDaysUntilExam = () => {
    const examDate = getExamDate();
    if (!examDate) return null;
    const diff = examDate.getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getProgress = () => {
    if (!germanStream) return 0;
    return Math.round((germanStream.completed_hours / germanStream.target_hours) * 100);
  };

  const getWeeklyHours = () => {
    const last7Days = logs.filter(log => {
      const logDate = new Date(log.date);
      const daysAgo = (new Date().getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 7;
    });
    return last7Days.reduce((sum, log) => sum + log.hours_studied, 0);
  };

  const weeklyHoursData = () => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      const dateStr = date.toISOString().split('T')[0];
      const dayLogs = logs.filter(log => log.date === dateStr);
      const hours = dayLogs.reduce((sum, log) => sum + log.hours_studied, 0);
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        hours: hours,
      };
    });
    return last7Days;
  };

  if (loading) {
    return <div className="p-6">Loading German tracker...</div>;
  }

  if (!germanStream) {
    return <div className="p-6">German learning stream not found. Please create it first.</div>;
  }

  const daysUntilExam = getDaysUntilExam();
  const progress = getProgress();
  const weeklyHours = getWeeklyHours();
  const weeklyTarget = 15;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            German Language Tracker
          </h1>
          <p className="text-gray-600">
            Goal: Reach Goethe Zertifikat B2 by September 2026
          </p>
        </div>

        {/* Main Progress Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Goethe B2 Certification Progress</h2>
              <p className="text-gray-600">
                Current Level: <span className="font-bold text-primary">{currentLevel}</span> | 
                Target Level: <span className="font-bold">B2</span>
              </p>
            </div>
            {daysUntilExam && daysUntilExam > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-600">Next Exam</p>
                <p className="text-2xl font-bold text-primary">{daysUntilExam} days</p>
                <p className="text-xs text-gray-500">{getExamDate()?.toLocaleDateString()}</p>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-6 mb-4">
            <div
              className="bg-primary h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
              style={{ width: `${Math.min(progress, 100)}%` }}
            >
              <span className="text-white text-sm font-medium">{progress}%</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div>
              <p className="text-sm text-gray-600">Total Hours</p>
              <p className="text-2xl font-bold">
                {germanStream.completed_hours.toFixed(1)} / {germanStream.target_hours}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-2xl font-bold">
                {weeklyHours.toFixed(1)} / {weeklyTarget}h
                {weeklyHours >= weeklyTarget && <span className="text-green-600 ml-2">✓</span>}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Vocabulary</p>
              <p className="text-2xl font-bold">
                {vocabularyWords} / {vocabularyTarget} words
              </p>
            </div>
          </div>
        </div>

        {/* Confidence Scores */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Skill Confidence Scores (1-10)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(confidenceScores).map(([skill, score]) => (
              <div key={skill} className="text-center">
                <p className="text-sm text-gray-600 capitalize mb-2">{skill}</p>
                <div className="relative w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-secondary h-4 rounded-full"
                    style={{ width: `${(score / 10) * 100}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                    {score}/10
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={score}
                  onChange={(e) => setConfidenceScores({
                    ...confidenceScores,
                    [skill]: parseInt(e.target.value),
                  })}
                  className="w-full mt-2"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Weekly Hours Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Weekly Study Hours</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyHoursData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="hours" stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setShowLogForm(!showLogForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {showLogForm ? 'Cancel' : 'Log Today\'s Study Hours'}
            </button>
            <button
              onClick={() => setShowVocabForm(!showVocabForm)}
              className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors"
            >
              {showVocabForm ? 'Cancel' : 'Record Vocabulary'}
            </button>
            <button
              onClick={() => setShowTestForm(!showTestForm)}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors"
            >
              {showTestForm ? 'Cancel' : 'Schedule Practice Test'}
            </button>
            <button
              onClick={() => setShowExamInfo(!showExamInfo)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {showExamInfo ? 'Close' : 'View Exam Info'}
            </button>
          </div>

          {showLogForm && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Log Study Hours</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Hours Studied</label>
                  <input
                    type="number"
                    step="0.5"
                    value={newLogHours}
                    onChange={(e) => setNewLogHours(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., 2.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Topics Covered (optional)</label>
                  <input
                    type="text"
                    value={newLogTopics}
                    onChange={(e) => setNewLogTopics(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Grammar, Vocabulary, Listening"
                  />
                </div>
                <button
                  onClick={handleLogHours}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  Save Log
                </button>
              </div>
            </div>
          )}

          {showVocabForm && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Add New Vocabulary</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">German Word *</label>
                  <input
                    type="text"
                    value={newVocab.word}
                    onChange={(e) => setNewVocab({ ...newVocab, word: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Haus"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">English Translation *</label>
                  <input
                    type="text"
                    value={newVocab.translation}
                    onChange={(e) => setNewVocab({ ...newVocab, translation: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., House"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Example Sentence</label>
                  <input
                    type="text"
                    value={newVocab.example_sentence}
                    onChange={(e) => setNewVocab({ ...newVocab, example_sentence: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Das ist mein Haus"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <input
                    type="text"
                    value={newVocab.category}
                    onChange={(e) => setNewVocab({ ...newVocab, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Living, Travel, Business"
                  />
                </div>
                <button
                  onClick={handleAddVocabulary}
                  className="w-full px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark"
                >
                  Add Vocabulary
                </button>
              </div>
            </div>
          )}

          {showTestForm && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Schedule Practice Test</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Test Name *</label>
                  <input
                    type="text"
                    value={newTest.test_name}
                    onChange={(e) => setNewTest({ ...newTest, test_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Mock Exam A1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Test Type</label>
                  <select
                    value={newTest.test_type}
                    onChange={(e) => setNewTest({ ...newTest, test_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Mock">Mock</option>
                    <option value="Goethe">Goethe</option>
                    <option value="TELC">TELC</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Skill Tested *</label>
                  <select
                    value={newTest.skill_tested}
                    onChange={(e) => setNewTest({ ...newTest, skill_tested: e.target.value as SkillType })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Listening">Listening</option>
                    <option value="Speaking">Speaking</option>
                    <option value="Reading">Reading</option>
                    <option value="Writing">Writing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scheduled Date *</label>
                  <input
                    type="date"
                    value={newTest.scheduled_date}
                    onChange={(e) => setNewTest({ ...newTest, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={newTest.duration_minutes}
                    onChange={(e) => setNewTest({ ...newTest, duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="60"
                  />
                </div>
                <button
                  onClick={handleScheduleTest}
                  className="w-full px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark"
                >
                  Schedule Test
                </button>
              </div>
            </div>
          )}

          {showExamInfo && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Goethe B2 Exam Information</h4>
              <div className="space-y-3">
                <div>
                  <h5 className="font-medium">Exam Structure:</h5>
                  <ul className="list-disc ml-5 text-sm text-gray-700">
                    <li>Reading (80 min) - 4 tasks</li>
                    <li>Listening (30 min) - 2 tasks</li>
                    <li>Writing (75 min) - 2 tasks</li>
                    <li>Speaking (15 min) - 2 tasks</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium">Pass Requirements:</h5>
                  <p className="text-sm text-gray-700">60% overall (180/300 points)</p>
                </div>
                <div>
                  <h5 className="font-medium">Exam Fees:</h5>
                  <p className="text-sm text-gray-700">₹13,500 (approx.)</p>
                </div>
                <div>
                  <h5 className="font-medium">Next Available Dates:</h5>
                  <ul className="list-disc ml-5 text-sm text-gray-700">
                    <li>March 15, 2025</li>
                    <li>June 20, 2025</li>
                    <li>September 15, 2025</li>
                  </ul>
                </div>
                <div>
                  <a
                    href="https://www.goethe.de/ins/in/en/sta/ban.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                  >
                    Visit Goethe-Institut Bangalore
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Resources */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Learning Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="https://www.goethe.de" target="_blank" rel="noopener noreferrer" className="p-4 border rounded-lg hover:bg-gray-50">
              <h4 className="font-semibold">Goethe-Institut</h4>
              <p className="text-sm text-gray-600">Official exam information</p>
            </a>
            <a href="https://www.duolingo.com" target="_blank" rel="noopener noreferrer" className="p-4 border rounded-lg hover:bg-gray-50">
              <h4 className="font-semibold">Duolingo</h4>
              <p className="text-sm text-gray-600">Daily practice</p>
            </a>
            <a href="https://www.youtube.com/@easygerman" target="_blank" rel="noopener noreferrer" className="p-4 border rounded-lg hover:bg-gray-50">
              <h4 className="font-semibold">Easy German (YouTube)</h4>
              <p className="text-sm text-gray-600">Listening practice</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GermanTracker;

